/** A tag read from typed or pasted text, with the share a trailing "NN%" gave it. */
export interface TagEntry {
  name: string
  percentage: number | null
}

interface ParseOptions {
  /** Names kept whole although they contain commas; a match takes their spelling. */
  protectedNames?: string[]
  /** Reads a trailing "NN%" off each part, as pasted shares carry one. */
  withShares?: boolean
  maxLength?: number
}

const BREAKS = /[\r\n;]+/
const SHARE_SUFFIX = /[\s:–-]*\(?\s*(\d{1,3}(?:\.\d+)?)\s*%\s*\)?\s*$/

/** Compares names regardless of case, outer spaces and the spacing around commas. */
export function tagKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
}

function readEntry(raw: string, withShares: boolean): TagEntry {
  const text = raw.trim()
  const match = withShares ? SHARE_SUFFIX.exec(text) : null
  if (!match) return { name: text, percentage: null }
  return { name: text.slice(0, match.index).trim(), percentage: Math.min(100, Math.round(Number(match[1]))) }
}

/** Splits text on line breaks, semicolons and commas; a comma inside a protected name doesn't split. */
export function parseTagText(
  text: string,
  { protectedNames = [], withShares = false, maxLength = 100 }: ParseOptions = {}
): TagEntry[] {
  const known = new Map(protectedNames.map((name) => [tagKey(name), name.trim()]))
  const seen = new Set<string>()
  const entries: TagEntry[] = []
  for (const segment of text.split(BREAKS)) {
    const parts = segment.split(',')
    let start = 0
    while (start < parts.length) {
      // Longest run of comma-separated parts that reads as one protected name, else a single part.
      let end = parts.length
      let entry = readEntry(parts.slice(start, end).join(','), withShares)
      while (end > start + 1 && !known.has(tagKey(entry.name))) {
        end -= 1
        entry = readEntry(parts.slice(start, end).join(','), withShares)
      }
      const name = (known.get(tagKey(entry.name)) ?? entry.name).slice(0, maxLength).trim()
      if (name && !seen.has(tagKey(name))) {
        seen.add(tagKey(name))
        entries.push({ name, percentage: entry.percentage })
      }
      start = end
    }
  }
  return entries
}

/** For typed text: a separator commits what is before it, unless that comma may still become a protected name. */
export function splitTypedText(
  text: string,
  protectedNames: string[] = []
): { complete: string; rest: string } {
  const lastBreak = Math.max(text.lastIndexOf(';'), text.lastIndexOf('\n'))
  const pieces = lastBreak >= 0 ? [text.slice(0, lastBreak)] : []
  const tail = text.slice(lastBreak + 1)
  const keys = protectedNames.map(tagKey)
  let start = 0
  for (let comma = tail.indexOf(','); comma !== -1; comma = tail.indexOf(',', comma + 1)) {
    const typed = tagKey(tail.slice(start, comma + 1))
    if (keys.some((key) => key.startsWith(typed))) continue
    pieces.push(tail.slice(start, comma))
    start = comma + 1
  }
  return { complete: pieces.join(';'), rest: tail.slice(start).trimStart() }
}

/** Adds names that aren't there yet (case-insensitively), trimmed and cut to maxLength, until maxTags. */
export function appendTags(
  tags: string[],
  names: string[],
  { maxTags, maxLength }: { maxTags: number; maxLength: number }
): string[] {
  const next = [...tags]
  const seen = new Set(tags.map(tagKey))
  for (const raw of names) {
    if (next.length >= maxTags) break
    const name = raw.trim().slice(0, maxLength).trim()
    if (!name || seen.has(tagKey(name))) continue
    seen.add(tagKey(name))
    next.push(name)
  }
  return next
}

/** Keeps entries for tags already there (in their spelling) and as many new ones as maxTags leaves room for. */
export function fitEntries(tags: string[], entries: TagEntry[], maxTags: number): TagEntry[] {
  const existing = new Map(tags.map((tag) => [tagKey(tag), tag]))
  let room = maxTags - tags.length
  const kept: TagEntry[] = []
  for (const entry of entries) {
    const known = existing.get(tagKey(entry.name))
    if (known !== undefined) {
      kept.push({ name: known, percentage: entry.percentage })
    } else if (room > 0) {
      kept.push(entry)
      room -= 1
    }
  }
  return kept
}
